import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABELS, canManage, isCorporate } from "@/context/AuthContext";

const CORPORATE_NAV = [
  { to: "/app/corporate", label: "Corporate Dashboard", icon: Buildings },
  { to: "/app/corporate/vehicles", label: "All Vehicles", icon: Cube },
  { to: "/app/scan", label: "Scan", icon: QrCode },
];
import { mediaUrl } from "@/components/AssetImage";
import {
  SquaresFour, Cube, MapPin, QrCode, ArrowsLeftRight, ChartBar, Users,
  Gear, CreditCard, SignOut, List, X, Plus, MagnifyingGlass, Buildings, ChatCircleDots, FileText, ChartLineUp,
} from "@phosphor-icons/react";
import { Printer, Tray } from "@phosphor-icons/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/app/inventory", label: "Inventory", icon: Cube },
  { to: "/app/locations", label: "Locations", icon: MapPin },
  { to: "/app/qr-codes", label: "QR Codes", icon: QrCode },
  { to: "/app/movement", label: "Movement", icon: ArrowsLeftRight },
  { to: "/app/reports", label: "Reports", icon: ChartBar },
  { to: "/app/insights", label: "Insights", icon: ChartLineUp, manage: true },
  { to: "/app/users", label: "Users", icon: Users },
  { to: "/app/settings", label: "Company Settings", icon: Gear },
  { to: "/app/billing", label: "Billing", icon: CreditCard },
];

const MOBILE_NAV = [
  { to: "/app/dashboard", label: "Home", icon: SquaresFour },
  { to: "/app/inventory", label: "Inventory", icon: Cube },
  { to: "/app/scan", label: "Scan", icon: QrCode, primary: true },
  { to: "/app/movement", label: "Moves", icon: ArrowsLeftRight },
  { to: "/app/reports", label: "Reports", icon: ChartBar },
];

export default function DashboardLayout({ children }) {
  const { user, company, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const writeAccess = user && ["super_admin", "company_admin", "manager", "employee"].includes(user.role);

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-zinc-950 text-zinc-300 w-64">
      <div className="px-5 py-5 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          {company?.logo_url ? (
            <img src={mediaUrl(company.logo_url)} alt="Logo" className="h-8 w-8 rounded-md object-cover bg-white" data-testid="sidebar-logo" />
          ) : (
            <div className="h-8 w-8 rounded-md bg-emerald-500 flex items-center justify-center">
              <Cube size={18} weight="bold" className="text-zinc-950" />
            </div>
          )}
          <span className="font-display font-bold text-white text-lg tracking-tight truncate">{company?.name || "Sterling Automotive Group"}</span>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {(isCorporate(user?.role) ? CORPORATE_NAV : user?.role === "super_admin" ? [{ to: "/app/admin", label: "Companies", icon: Buildings }, { to: "/app/admin/groups", label: "Corporate Groups", icon: Buildings }, { to: "/app/admin/leads", label: "Leads", icon: Tray }, { to: "/app/admin/crm", label: "Dealership CRM", icon: ChartLineUp }, { to: "/app/admin/demo-requests", label: "Demo Requests", icon: ChatCircleDots }, { to: "/app/admin/prospects", label: "Prospect Scores", icon: ChartLineUp }, { to: "/app/admin/quotes", label: "Label Quotes", icon: Printer }, { to: "/app/admin/contacts", label: "Website Questions", icon: ChatCircleDots }, { to: "/app/admin/traffic", label: "Site Traffic", icon: ChartLineUp }, ...NAV, { to: "/app/admin/legal", label: "Legal Pages", icon: FileText }] : NAV).filter((item) => !item.manage || canManage(user?.role)).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 h-11 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-emerald-500 text-zinc-950" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
              }`
            }
          >
            <item.icon size={20} weight="bold" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-zinc-800">
        <div className="px-3 py-2 mb-2">
          <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
          <p className="text-xs text-zinc-500">{ROLE_LABELS[user?.role]}</p>
          <p className="text-xs text-emerald-500 truncate mt-0.5">{company?.name}</p>
        </div>
        {actingId && (
          <button
            onClick={exitImpersonation}
            data-testid="sidebar-exit-impersonation"
            className="flex items-center gap-3 px-3 h-10 w-full rounded-md text-sm font-semibold text-amber-400 hover:bg-zinc-900 hover:text-amber-300 transition-colors mb-1"
          >
            <Buildings size={20} weight="bold" /> Exit to Admin
          </button>
        )}
        <button
          onClick={logout}
          data-testid="logout-button"
          className="flex items-center gap-3 px-3 h-10 w-full rounded-md text-sm font-medium text-zinc-400 hover:bg-zinc-900 hover:text-white transition-colors"
        >
          <SignOut size={20} weight="bold" /> Sign Out
        </button>
      </div>
    </div>
  );

  const actingId = localStorage.getItem("aim_active_company");
  const actingName = localStorage.getItem("aim_active_company_name");
  const exitImpersonation = () => {
    localStorage.removeItem("aim_active_company");
    localStorage.removeItem("aim_active_company_name");
    window.location.href = "/app/admin";
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFB]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-30">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative z-10">
            <Sidebar />
          </div>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        {actingId && (
          <div className="bg-amber-500 text-white text-sm font-semibold px-4 h-9 flex items-center justify-between" data-testid="impersonation-banner">
            <span className="truncate">Acting on behalf of {actingName || "a company"}</span>
            <button onClick={exitImpersonation} className="underline shrink-0 ml-3" data-testid="exit-impersonation">Exit</button>
          </div>
        )}
        {/* Top bar */}
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-sidebar-button">
              <List size={26} />
            </button>
            <span className="lg:hidden font-display font-bold text-base tracking-tight truncate max-w-[180px]">{company?.name || "Orbit"}</span>
          </div>
          <div className="flex items-center gap-2">
            {writeAccess && (
              <Button
                variant="outline"
                className="h-10 gap-2 border-zinc-300"
                onClick={() => navigate("/app/inventory?new=1")}
                data-testid="topbar-add-asset"
              >
                <Plus size={18} weight="bold" /> <span className="hidden sm:inline">Add Asset</span>
              </Button>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 pb-24 lg:pb-6">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-zinc-200 flex items-stretch h-16 pb-[env(safe-area-inset-bottom)]">
          {(isCorporate(user?.role) ? CORPORATE_NAV : MOBILE_NAV).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${
                  item.primary
                    ? "text-emerald-600"
                    : isActive
                    ? "text-emerald-600"
                    : "text-zinc-500"
                }`
              }
            >
              {item.primary ? (
                <div className="h-9 w-9 -mt-4 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
                  <item.icon size={22} weight="bold" className="text-white" />
                </div>
              ) : (
                <item.icon size={22} weight="bold" />
              )}
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
